import React, { useEffect, useState } from "react";
import { paymentCreateBankingAlias, paymentCreateWallets, paymentSubmitKYC } from "api";
import { useFormattedMessage } from "hooks";
import NewLoader from "components/NewLoader";

import ExplanationRedIcon from "assets/images/svg/exclamation-red.svg";

const messages = {
    pleaseWaitWhileSetupEwallet: "please_wait_while_setup_ewallet",
    errorWhileCreatingWallet: "error_while_creating_wallet",
    couldntCreateWallet: "couldnt_create_wallet",
    errorWhileCreatingBankingAlias: "error_while_creating_banking_alias",
    couldntCreateBankingAlias: "couldnt_create_banking_alias",
    errorWhileSubmittingKYC: "error_while_submitting_kyc",
    couldntSubmitKYC: "couldnt_submit_kyc",
    retry: "retry",
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const SetupEwallet = ({ open, backSlideEffect = "", setBackSlideEffect, setStep, apiCall }) => {
    const { formatMessage } = useFormattedMessage();

    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState({});

    const bypassSuccessErrors = [
        "resolver.Payment.CreateWallets: failed to create 'payin' wallet: mangopay_wallet_already_created",
        "resolver.Payment.CreateBankingAlias: mangopay_banking_alias_already_available",
        "resolver.Payment.SubmitKYC: failed to submit kyc: mangopay_kyc_already_completed",
    ];

    const errorInfo = {
        createWallets: {
            label: formatMessage(messages.errorWhileCreatingWallet),
            content: formatMessage(messages.couldntCreateWallet),
            retryFn: () => createWallets(),
        },
        createBankingAlias: {
            label: formatMessage(messages.errorWhileCreatingBankingAlias),
            content: formatMessage(messages.couldntCreateBankingAlias),
            retryFn: () => createBankingAlias(),
        },
        submitKYC: {
            label: formatMessage(messages.errorWhileSubmittingKYC),
            content: formatMessage(messages.couldntSubmitKYC),
            retryFn: () => submitKYC(),
        },
    };

    const runStep = async (step, apiFn, nextStep) => {
        try {
            setLoading(true);

            const res = await apiFn();

            if (res?.data?.errors?.length && !bypassSuccessErrors.includes(res?.data?.errors?.[0]?.message)) {
                setError(errorInfo[step]);
                setLoading(false);
                return;
            }

            if (step === "submitKYC") {
                setStep("ewalletSuccess");
                setBackSlideEffect("step-slide-left");
                return;
            }

            if (nextStep) {
                await delay(500);
                await nextStep();
            }
        } catch (err) {
            console.error(`${step} err`, err);
            setLoading(false);
        }
    };

    const createWallets = () => runStep("createWallets", paymentCreateWallets, createBankingAlias);

    const createBankingAlias = () => runStep("createBankingAlias", paymentCreateBankingAlias, submitKYC);

    const submitKYC = () => runStep("submitKYC", paymentSubmitKYC);

    const apiNeedsToCall = { createWallets, createBankingAlias, submitKYC };

    useEffect(() => {
        if (open && apiCall && apiNeedsToCall[apiCall]) {
            apiNeedsToCall[apiCall]();
        }

        if (!open) {
            setLoading(true);
            setError({});
        }
    }, [open, apiCall]);

    return (
        <div
            className={`px-5 ${backSlideEffect}`}
            style={{ position: "absolute", top: "43%", left: 0, right: 0, transform: "translateY(-43%)" }}
        >
            <div className="text-center">
                <div className="mb-4">{isLoading ? <NewLoader /> : <ExplanationRedIcon />}</div>
                {isLoading ? (
                    <div className="px-24">
                        <span className="text-16">{formatMessage(messages.pleaseWaitWhileSetupEwallet)}</span>
                    </div>
                ) : (
                    <div>
                        <span className="text-16 d-block mb-4">{error?.label}</span>
                        <span className="text-16 d-block mb-6">{error?.content}</span>
                        <button className="medium" onClick={error?.retryFn}>
                            {formatMessage(messages.retry)}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SetupEwallet;
